import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import type { ApiError } from '../types/common';

// Types
export interface Review {
  id: number;
  bookingId?: number;
  therapistId?: number;
  consumerId?: number;
  rating: number;
  comment?: string;
  isAnonymous: boolean;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  updatedAt?: string;
}

export interface ReviewStats {
  totalReviews: number;
  avgRating: number;
  fiveStarCount: number;
  fourStarCount: number;
  threeStarCount: number;
  twoStarCount: number;
  oneStarCount: number;
  fiveStarPercentage: number;
  fourStarPercentage: number;
  threeStarPercentage: number;
  twoStarPercentage: number;
  oneStarPercentage: number;
}

export interface SubmitReviewRequest {
  rating: number;
  comment?: string;
  isAnonymous?: boolean;
}

export interface ReviewsResponse {
  reviews: Review[];
  total: number;
  limit: number;
  offset: number;
}

export interface RejectReviewRequest {
  reason: string;
}

// Query keys
const reviewKeys = {
  all: ['reviews'] as const,
  therapist: (therapistId: number) => [...reviewKeys.all, 'therapist', therapistId] as const,
  therapistStats: (therapistId: number) => [...reviewKeys.all, 'stats', therapistId] as const,
  pending: () => [...reviewKeys.all, 'pending'] as const,
};

// ============================================
// Consumer Endpoints
// ============================================

/**
 * Submit a review for a completed booking
 */
async function submitReview(bookingId: number, data: SubmitReviewRequest): Promise<Review> {
  const response = await apiClient.post<Review>(`/reviews/bookings/${bookingId}/review`, data);
  return response.data;
}

export function useSubmitReview() {
  const queryClient = useQueryClient();

  return useMutation<Review, ApiError, { bookingId: number; data: SubmitReviewRequest }>({
    mutationFn: ({ bookingId, data }) => submitReview(bookingId, data),
    onSuccess: (review) => {
      // Invalidate therapist reviews to show new review once approved
      if (review.therapistId) {
        queryClient.invalidateQueries({ queryKey: reviewKeys.therapist(review.therapistId) });
        queryClient.invalidateQueries({ queryKey: reviewKeys.therapistStats(review.therapistId) });
      }
    },
    onError: (error) => {
      console.error('Failed to submit review:', error);
    },
  });
}

/**
 * Delete own review (only pending reviews)
 */
async function deleteReview(reviewId: number): Promise<void> {
  await apiClient.delete(`/reviews/${reviewId}`);
}

export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, number>({
    mutationFn: deleteReview,
    onSuccess: () => {
      // Invalidate all review queries
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
    onError: (error) => {
      console.error('Failed to delete review:', error);
    },
  });
}

// ============================================
// Public Endpoints
// ============================================

/**
 * Get approved reviews for a therapist
 */
async function getTherapistReviews(
  therapistId: number,
  limit: number = 20,
  offset: number = 0
): Promise<ReviewsResponse> {
  const response = await apiClient.get<ReviewsResponse>(
    `/reviews/therapists/${therapistId}/reviews`,
    {
      params: { limit, offset },
    }
  );
  return response.data;
}

export function useTherapistReviews(therapistId: number, limit: number = 20, offset: number = 0) {
  return useQuery<ReviewsResponse, ApiError>({
    queryKey: [...reviewKeys.therapist(therapistId), limit, offset],
    queryFn: () => getTherapistReviews(therapistId, limit, offset),
    enabled: !!therapistId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get rating statistics for a therapist
 */
async function getTherapistStats(therapistId: number): Promise<ReviewStats> {
  const response = await apiClient.get<ReviewStats>(`/reviews/therapists/${therapistId}/stats`);
  return response.data;
}

export function useTherapistStats(therapistId: number) {
  return useQuery<ReviewStats, ApiError>({
    queryKey: reviewKeys.therapistStats(therapistId),
    queryFn: () => getTherapistStats(therapistId),
    enabled: !!therapistId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================
// Admin Endpoints
// ============================================

/**
 * Get pending reviews for moderation
 */
async function getPendingReviews(limit: number = 50, offset: number = 0): Promise<ReviewsResponse> {
  const response = await apiClient.get<ReviewsResponse>('/reviews/admin/pending', {
    params: { limit, offset },
  });
  return response.data;
}

export function usePendingReviews(limit: number = 50, offset: number = 0) {
  return useQuery<ReviewsResponse, ApiError>({
    queryKey: [...reviewKeys.pending(), limit, offset],
    queryFn: () => getPendingReviews(limit, offset),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Approve a review
 */
async function approveReview(reviewId: number): Promise<Review> {
  const response = await apiClient.put<Review>(`/reviews/admin/${reviewId}/approve`);
  return response.data;
}

/**
 * Reject a review
 */
async function rejectReview(reviewId: number, data: RejectReviewRequest): Promise<Review> {
  const response = await apiClient.put<Review>(`/reviews/admin/${reviewId}/reject`, data);
  return response.data;
}

export function useModerateReview() {
  const queryClient = useQueryClient();

  const approve = useMutation<Review, ApiError, number>({
    mutationFn: approveReview,
    onSuccess: (review) => {
      // Invalidate pending reviews
      queryClient.invalidateQueries({ queryKey: reviewKeys.pending() });
      // Invalidate therapist reviews and stats
      if (review.therapistId) {
        queryClient.invalidateQueries({ queryKey: reviewKeys.therapist(review.therapistId) });
        queryClient.invalidateQueries({ queryKey: reviewKeys.therapistStats(review.therapistId) });
      }
    },
    onError: (error) => {
      console.error('Failed to approve review:', error);
    },
  });

  const reject = useMutation<Review, ApiError, { reviewId: number; data: RejectReviewRequest }>({
    mutationFn: ({ reviewId, data }) => rejectReview(reviewId, data),
    onSuccess: () => {
      // Invalidate pending reviews
      queryClient.invalidateQueries({ queryKey: reviewKeys.pending() });
    },
    onError: (error) => {
      console.error('Failed to reject review:', error);
    },
  });

  return { approve, reject };
}
